import { useEffect, useMemo, useRef } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { X } from "@phosphor-icons/react";

const formatTimestamp = (value) => {
  if (!value) return "Unknown time";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

export default function EvidenceDrawer({ open, obligation, events = [], onOpenChange }) {
  const replyRef = useRef(null);
  const closeRef = useRef(null);
  const previousFocusRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    previousFocusRef.current = document.activeElement;
    const onKeyDown = (event) => {
      if (event.key === "Escape") onOpenChange(false);
    };
    document.addEventListener("keydown", onKeyDown);
    const timer = window.setTimeout(() => closeRef.current?.focus(), 20);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("keydown", onKeyDown);
      previousFocusRef.current?.focus?.();
    };
  }, [onOpenChange, open]);

  const payloadText = useMemo(() => {
    const sourcePayload = obligation?.sourcePayload || obligation?.raw?.source_payload || obligation?.raw?.payload;
    if (!sourcePayload) return "";
    try {
      return JSON.stringify(sourcePayload, null, 2);
    } catch {
      return String(sourcePayload);
    }
  }, [obligation]);

  if (!open || !obligation) return null;

  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        aria-label="Close evidence drawer"
        className="absolute inset-0 bg-[rgba(17,32,58,0.18)]"
        onClick={() => onOpenChange(false)}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={`Evidence for ${obligation.title || obligation.provenance?.origin || "obligation"}`}
        className="absolute right-0 top-0 flex h-full w-[min(92vw,28.75rem)] flex-col border-l border-[hsl(var(--input))] bg-card shadow-[-16px_0_44px_rgba(17,32,58,0.18)]"
      >
        <div className="border-b border-[hsl(var(--border))] px-6 py-5">
          <div className="flex items-start gap-4">
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[hsl(var(--muted-foreground))]">
                Original source
              </p>
              <p className="mt-1 font-serif text-[1.08rem] font-medium text-[hsl(var(--foreground))]">
                {obligation?.provenance?.origin || obligation?.title}
              </p>
              <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">
                {[obligation?.asset, obligation?.counterparty].filter(Boolean).join(" · ") || "Source detail"}
              </p>
            </div>
            <button
              ref={closeRef}
              type="button"
              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[hsl(var(--muted-foreground))] transition-colors hover:bg-[hsl(var(--secondary))]"
              onClick={() => onOpenChange(false)}
            >
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col bg-[hsl(var(--background))]">
          <div className="flex-1 overflow-y-auto px-6 py-5">
            {obligation?.rawText ? (
              <section className="mb-5 rounded-[0.9rem] border border-[hsl(var(--border))] bg-card p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[hsl(var(--muted-foreground))]">Captured text</p>
                <p className="mt-3 text-sm leading-6 text-[hsl(var(--foreground))]">{obligation.rawText}</p>
              </section>
            ) : null}

            {events.length ? (
              <section className="mb-5">
                <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[hsl(var(--muted-foreground))]">Event record</p>
                <div className="mt-3 space-y-3">
                  {events.map((event) => (
                    <div key={event.id} className="rounded-[0.9rem] border border-[hsl(var(--border))] bg-card p-4">
                      <div className="flex items-start justify-between gap-3">
                        <p className="text-sm font-medium text-[hsl(var(--foreground))]">{event.type}</p>
                        <p className="text-[11px] text-[hsl(var(--muted-foreground))]">{formatTimestamp(event.timestamp)}</p>
                      </div>
                      {event.snippet ? (
                        <p className="mt-2 text-sm leading-6 text-[hsl(var(--muted-foreground))]">{event.snippet}</p>
                      ) : null}
                      {event.payload ? (
                        <pre className="mt-3 overflow-x-auto rounded-[0.75rem] bg-[hsl(var(--secondary))] p-3 text-[11px] leading-5 text-[hsl(var(--foreground))]">
                          {JSON.stringify(event.payload, null, 2)}
                        </pre>
                      ) : null}
                    </div>
                  ))}
                </div>
              </section>
            ) : null}

            {obligation?.evidenceRefs?.length ? (
              <section className="mb-5">
                <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[hsl(var(--muted-foreground))]">Evidence refs</p>
                <div className="mt-3 space-y-3">
                  {obligation.evidenceRefs.map((ref, index) => (
                    <div key={`${index}-${String(ref)}`} className="rounded-[0.75rem] border border-[hsl(var(--border))] bg-card px-4 py-3 text-sm text-[hsl(var(--foreground))]">
                      {typeof ref === "string" ? ref : JSON.stringify(ref)}
                    </div>
                  ))}
                </div>
              </section>
            ) : null}

            {payloadText ? (
              <section>
                <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[hsl(var(--muted-foreground))]">Payload</p>
                <pre className="mt-3 overflow-x-auto rounded-[0.9rem] border border-[hsl(var(--border))] bg-card p-4 text-[11px] leading-5 text-[hsl(var(--foreground))]">
                  {payloadText}
                </pre>
              </section>
            ) : null}
          </div>

          <div className="border-t border-[hsl(var(--border))] bg-card px-6 py-4">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.08em] text-[hsl(var(--muted-foreground))]">Reply in place</p>
            <Textarea
              ref={replyRef}
              placeholder="Reply routes out in a later round"
              className="min-h-[84px] rounded-[0.625rem] border-[hsl(var(--input))] bg-white text-sm"
            />
            <div className="mt-3 flex items-center justify-between gap-3">
              <p className="text-xs text-[hsl(var(--muted-foreground))]">Reply routes out in a later round</p>
              <Button type="button" disabled className="rounded-[0.625rem]">
                Send
              </Button>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}
